import { DEFAULT_PRICING_RULES, ruleValue } from '@repo/db/pricing-rules'

/**
 * How the gear reaches the customer, when the clock starts, and what being late
 * costs (spec §11 · I6).
 *
 * > Dostawa: wybór w koszyku — odbiór osobisty (Wrocław 9 do 18) albo kurier
 * > InPost (wysyłka i zwrot).
 * > Start najmu (odbiór): od godziny odbioru w siedzibie.
 * > Start najmu (kurier): od statusu Dostarczone w aplikacji InPost.
 * > Kara za opóźnienie: 200 procent stawki bazowej dziennej za każdy dzień.
 *
 * ── THE HIRE IS MEASURED FROM A HAND-OVER, NOT FROM A DATE ───────────────
 * This is the sentence that shapes the whole file. A booking has a `startsAt`
 * — the day the customer picked — but spec §11 does not bill from it. It bills
 * from the moment the equipment was actually in their hands: the hour they
 * walked out of the office, or the minute InPost said "Dostarczone". The two
 * differ constantly, and always in the customer's favour when a courier is
 * late, which is the correct direction.
 *
 * The spec's own worked example is the first test in `delivery.test.ts`: a
 * one-day hire delivered at 15:00 comes back by 15:00 the next day. Not "by the
 * end of the next day", and not "by 09:00" — twenty-four hours from the hand-
 * over, which is what "doba" means.
 *
 * ── AND THE PENALTY IS DELIBERATELY PUNITIVE ─────────────────────────────
 * 200 % of the daily rate per day late is not compensation, it is a deterrent:
 * a camera that does not come back on Friday is a camera FLOU has already
 * promised to somebody for Saturday, so the cost of a late return is not one
 * day's rental — it is one day's rental plus a hire nobody can fulfil.
 *
 * Pure: instants and grosze in, instants and grosze out.
 */

/** How the gear travels. The customer picks one for the whole basket. */
export const DELIVERY_METHODS = ['PICKUP', 'COURIER'] as const
export type DeliveryMethod = (typeof DELIVERY_METHODS)[number]

/** Spec §11: "odbiór osobisty (Wrocław 9 do 18)". */
export const PICKUP_OPENS_MINUTES = 9 * 60
export const PICKUP_CLOSES_MINUTES = 18 * 60

/**
 * Spec §11: "200 procent stawki bazowej dziennej za każdy dzień opóźnienia".
 *
 * The default for `rental.late_fee_multiplier`, which Cennik owns. The pickup
 * hours above it stay constants — they are when the office is open, not a price.
 */
export const LATE_FEE_MULTIPLIER = ruleValue(DEFAULT_PRICING_RULES, 'rental.late_fee_multiplier')

const DAY_MS = 24 * 60 * 60 * 1000

export function deliveryLabel(method: DeliveryMethod): string {
  return method === 'COURIER' ? 'Kurier InPost' : 'Odbiór osobisty (Wrocław)'
}

/**
 * When the hire actually started — or `null` while it has not.
 *
 * `handedOverAt` is the one fact both methods reduce to: the hour of collection
 * at the office, or the "Dostarczone" timestamp from InPost. Modelled as ONE
 * column (`booking.delivered_at`) rather than two, because every rule
 * downstream — the return deadline, the late fee, whether the hire may still be
 * cancelled — asks the same question of both, and two columns would be two
 * places for the answer to be missing.
 */
export function hireStartsAt(handedOverAt: Date | null): Date | null {
  return handedOverAt
}

/**
 * When it has to be back: twenty-four hours per paid day, from the hand-over.
 *
 * Spec §11's worked example, exactly: a one-day hire delivered at 15:00 is due
 * back at 15:00 the next day. Counting calendar days instead would make the
 * same hire due at midnight — eight hours the customer paid for and did not
 * get — and counting from the booked `startsAt` would charge them for a
 * courier's delay.
 */
export function dueBackAt(handedOverAt: Date, days: number): Date {
  return new Date(handedOverAt.getTime() + Math.max(1, Math.trunc(days)) * DAY_MS)
}

/**
 * Whole days late, rounded UP — or 0 when it came back in time.
 *
 * Up, because a day is the unit the penalty is expressed in and there is no
 * such thing as a third of a late day: the camera missed Saturday whether it
 * came back at 09:00 or at 23:00. Rounding down would make every return before
 * midnight free, which is the same as having no deadline.
 */
export function lateDays(dueBackAt: Date, returnedAt: Date): number {
  const over = returnedAt.getTime() - dueBackAt.getTime()
  if (over <= 0) return 0
  return Math.ceil(over / DAY_MS)
}

/**
 * Spec §11's penalty: 200 % of the BASE daily rate for every day late.
 *
 * "Stawka bazowa dzienna" and not the threshold price the customer paid — a
 * thirty-day hire is heavily discounted per day, and letting the penalty
 * inherit that discount would make a long hire the cheapest one to keep. The
 * spec's own example is unambiguous: gear at 100 zł for one day is 200 zł for
 * every day of delay.
 */
export function lateFeeMinor(
  dailyRateMinor: number,
  days: number,
  multiplier: number = LATE_FEE_MULTIPLIER,
): number {
  if (days <= 0) return 0
  return Math.max(0, Math.trunc(dailyRateMinor)) * multiplier * Math.trunc(days)
}

/** The whole late-return verdict, for the screen that has to explain it. */
export interface LateReturn {
  readonly days: number
  readonly feeNetMinor: number
  readonly dueBackAt: Date
}

/** What a return that has already happened comes to. */
export function settleReturn(input: {
  readonly handedOverAt: Date
  readonly paidDays: number
  readonly returnedAt: Date
  readonly dailyRateMinor: number
  /** The multiplier frozen onto this booking, when it carries one. */
  readonly lateFeeMultiplier?: number
}): LateReturn {
  const due = dueBackAt(input.handedOverAt, input.paidDays)
  const days = lateDays(due, input.returnedAt)
  return {
    days,
    feeNetMinor: lateFeeMinor(input.dailyRateMinor, days, input.lateFeeMultiplier),
    dueBackAt: due,
  }
}

/**
 * Why this hand-over cannot be recorded — or `null` when it can.
 *
 * The office types these from what happened, so the refusals are about
 * IMPOSSIBLE sequences rather than about permission: gear returned before it
 * went out, or handed over twice.
 */
export function handOverProblem(
  booking: { readonly deliveredAt: Date | null; readonly status: string },
  at: Date,
  now: Date,
): string | null {
  if (booking.status === 'CANCELLED' || booking.status === 'NO_SHOW') {
    return 'Ta rezerwacja jest zamknięta — nic z niej nie wychodzi.'
  }
  if (booking.deliveredAt) return 'Ten sprzęt jest już wydany.'
  if (at.getTime() > now.getTime() + DAY_MS) {
    return 'Nie da się wydać sprzętu z datą z przyszłości.'
  }
  return null
}

/** …and the same for the return leg. */
export function returnProblem(
  booking: { readonly deliveredAt: Date | null; readonly returnedAt: Date | null },
  at: Date,
): string | null {
  if (!booking.deliveredAt) return 'Najpierw odnotuj wydanie sprzętu.'
  if (booking.returnedAt) return 'Ten sprzęt jest już odebrany.'
  if (at.getTime() < booking.deliveredAt.getTime()) {
    return 'Zwrot nie może być wcześniejszy niż wydanie.'
  }
  return null
}
